/**
 * Wire schemas shared by the auth server and the portal client.
 *
 * The server validates every request body against these; the client reuses them
 * for form validation so the two can never drift.
 */
import { z } from 'zod';
import {
  ACCESS_DENIAL_REASON,
  ENTITLEMENT_STATUS,
  MEMBERSHIP_STATUS,
  ORGANIZATION_ROLE,
  ORGANIZATION_STATUS,
  ORGANIZATION_TYPE,
  PLATFORM_ROLE,
  USER_STATUS,
} from './domain.js';

/**
 * Password policy. The existing MindPeer system stores bcrypt hashes and its
 * only documented rule is a minimum length, so we keep the floor compatible
 * (never rejecting a password an existing user already has) while requiring a
 * little more of newly chosen ones. bcrypt silently truncates at 72 bytes, so
 * that is a hard ceiling rather than an arbitrary limit.
 */
export const PASSWORD_MIN_LENGTH = 8;
export const PASSWORD_MAX_BYTES = 72;

export const passwordSchema = z
  .string()
  .min(PASSWORD_MIN_LENGTH, `Password must be at least ${PASSWORD_MIN_LENGTH} characters`)
  .refine(
    (value) => new TextEncoder().encode(value).length <= PASSWORD_MAX_BYTES,
    `Password must be at most ${PASSWORD_MAX_BYTES} bytes`,
  )
  .refine((value) => /[a-zA-Z]/.test(value), 'Password must contain a letter')
  .refine((value) => /[0-9]|[^a-zA-Z0-9]/.test(value), 'Password must contain a number or symbol');

/** Emails are stored as given and matched on a normalized (lowercased) copy. */
export const emailSchema = z
  .string()
  .trim()
  .min(3)
  .max(254)
  .email('Enter a valid email address');

const nameSchema = z.string().trim().min(1, 'Required').max(100);

/** Only relative, single-slash-prefixed paths may be echoed back (PRD §16). */
export const relativePathSchema = z
  .string()
  .max(2048)
  .regex(/^\/(?!\/)[^\\]*$/, 'Must be a relative path')
  .optional();

export const signupSchema = z
  .object({
    firstName: nameSchema,
    lastName: nameSchema,
    email: emailSchema,
    password: passwordSchema,
    passwordConfirmation: z.string(),
    acceptedTerms: z.literal(true, {
      errorMap: () => ({ message: 'You must accept the terms and privacy policy' }),
    }),
    /** Optional invitation token, when arriving from an invitation email. */
    invitationToken: z.string().max(512).optional(),
  })
  .refine((data) => data.password === data.passwordConfirmation, {
    message: 'Passwords do not match',
    path: ['passwordConfirmation'],
  });
export type SignupInput = z.infer<typeof signupSchema>;

export const loginSchema = z.object({
  email: emailSchema,
  password: z.string().min(1, 'Enter your password'),
  /** Extends the central session lifetime to the "remember me" duration. */
  rememberMe: z.boolean().optional().default(false),
});
export type LoginInput = z.infer<typeof loginSchema>;

export const verifyEmailSchema = z.object({
  token: z.string().min(16).max(512),
});
export type VerifyEmailInput = z.infer<typeof verifyEmailSchema>;

export const resendVerificationSchema = z.object({
  email: emailSchema,
});
export type ResendVerificationInput = z.infer<typeof resendVerificationSchema>;

export const forgotPasswordSchema = z.object({
  email: emailSchema,
});
export type ForgotPasswordInput = z.infer<typeof forgotPasswordSchema>;

export const resetPasswordSchema = z
  .object({
    token: z.string().min(16).max(512),
    password: passwordSchema,
    passwordConfirmation: z.string(),
  })
  .refine((data) => data.password === data.passwordConfirmation, {
    message: 'Passwords do not match',
    path: ['passwordConfirmation'],
  });
export type ResetPasswordInput = z.infer<typeof resetPasswordSchema>;

export const changePasswordSchema = z
  .object({
    currentPassword: z.string().min(1, 'Enter your current password'),
    password: passwordSchema,
    passwordConfirmation: z.string(),
  })
  .refine((data) => data.password === data.passwordConfirmation, {
    message: 'Passwords do not match',
    path: ['passwordConfirmation'],
  })
  .refine((data) => data.password !== data.currentPassword, {
    message: 'Choose a password different from your current one',
    path: ['password'],
  });
export type ChangePasswordInput = z.infer<typeof changePasswordSchema>;

export const setActiveOrganizationSchema = z.object({
  organizationId: z.string().min(1).max(128),
});
export type SetActiveOrganizationInput = z.infer<typeof setActiveOrganizationSchema>;

/* ------------------------------------------------------------------ *
 * Administrative payloads (PRD §15)
 * ------------------------------------------------------------------ */

export const createOrganizationSchema = z.object({
  name: z.string().trim().min(1, 'An organization name is required').max(200),
  /**
   * Optional. Derived from the name when omitted — an admin provisioning a
   * tenant should not have to think about URL-safe identifiers, but one is
   * accepted so a customer whose name transliterates badly can be corrected.
   */
  slug: z
    .string()
    .trim()
    .min(1)
    .max(60)
    .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'Use lowercase letters, numbers and single hyphens')
    .optional(),
  /**
   * Which application to entitle the new tenant for. Without an entitlement the
   * first invitee is denied at `/authorize` and their sign-in silently goes
   * nowhere, so the server always entitles for something.
   *
   * Deliberately carries no default here. Core's client id differs per
   * environment (`mindpeer` in production, `mindpeer-dev` in dev), and a
   * constant in a shared package cannot know which issuer it is talking to —
   * a literal default sent the dev panel on to entitle a tenant for an
   * application that is not registered there. The server fills it in from
   * `CORE_CLIENT_ID` when omitted.
   */
  applicationId: z.string().trim().min(1).max(64).optional(),
});
export type CreateOrganizationInput = z.infer<typeof createOrganizationSchema>;

export const createInvitationSchema = z.object({
  email: emailSchema,
  /**
   * Defaults to `admin`, not `member`: this endpoint exists to hand a new
   * customer's tenant to its own administrator, and a first invitee who cannot
   * invite anyone else leaves the tenant unusable without us intervening again.
   */
  roles: z.array(z.enum(ORGANIZATION_ROLE)).min(1).max(4).default(['admin']),
});
export type CreateInvitationInput = z.infer<typeof createInvitationSchema>;

export const invitationSummarySchema = z.object({
  id: z.string(),
  email: z.string(),
  roles: z.array(z.enum(ORGANIZATION_ROLE)),
  status: z.enum(['pending', 'accepted', 'revoked', 'expired']),
  expiresAt: z.string(),
  createdAt: z.string(),
  /**
   * The plaintext token, returned only on creation and never on a listing —
   * the stored form is a hash, so a list could not produce it anyway. Present
   * so a panel can offer a copyable link when mail delivery is unconfigured.
   */
  acceptUrl: z.string().optional(),
});
export type InvitationSummary = z.infer<typeof invitationSummarySchema>;

/* ------------------------------------------------------------------ *
 * Response payloads
 * ------------------------------------------------------------------ */

export const organizationSummarySchema = z.object({
  id: z.string(),
  name: z.string(),
  slug: z.string(),
  type: z.enum(ORGANIZATION_TYPE),
  status: z.enum(ORGANIZATION_STATUS),
});
export type OrganizationSummary = z.infer<typeof organizationSummarySchema>;

/** An organization as the admin panel lists it. */
export const adminOrganizationSchema = organizationSummarySchema.extend({
  joinPolicy: z.string(),
  memberCount: z.number(),
  createdAt: z.string(),
});
export type AdminOrganization = z.infer<typeof adminOrganizationSchema>;

export const membershipSummarySchema = z.object({
  organization: organizationSummarySchema,
  roles: z.array(z.enum(ORGANIZATION_ROLE)),
  status: z.enum(MEMBERSHIP_STATUS),
  isActive: z.boolean(),
});
export type MembershipSummary = z.infer<typeof membershipSummarySchema>;

/** `GET /api/me`. Never includes password material (PRD §16). */
export const meSchema = z.object({
  id: z.string(),
  email: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  emailVerified: z.boolean(),
  status: z.enum(USER_STATUS),
  platformRoles: z.array(z.enum(PLATFORM_ROLE)),
  activeOrganization: organizationSummarySchema.nullable(),
  createdAt: z.string(),
});
export type Me = z.infer<typeof meSchema>;

export const sessionSummarySchema = z.object({
  id: z.string(),
  current: z.boolean(),
  createdAt: z.string(),
  lastSeenAt: z.string(),
  expiresAt: z.string(),
  ipAddress: z.string().nullable(),
  userAgent: z.string().nullable(),
});
export type SessionSummary = z.infer<typeof sessionSummarySchema>;

export const entitlementSummarySchema = z.object({
  applicationId: z.string(),
  status: z.enum(ENTITLEMENT_STATUS),
  tier: z.string().nullable(),
  startsAt: z.string().nullable(),
  expiresAt: z.string().nullable(),
});
export type EntitlementSummary = z.infer<typeof entitlementSummarySchema>;

export const accessDenialSchema = z.object({
  reason: z.enum(ACCESS_DENIAL_REASON),
  message: z.string(),
  applicationId: z.string().nullable(),
  organizationId: z.string().nullable(),
});
export type AccessDenial = z.infer<typeof accessDenialSchema>;

/** Uniform error envelope for every non-2xx JSON response. */
export const apiErrorSchema = z.object({
  error: z.object({
    code: z.string(),
    message: z.string(),
    /** Field-level messages, keyed by dotted path, for form rendering. */
    fields: z.record(z.array(z.string())).optional(),
    requestId: z.string().optional(),
  }),
});
export type ApiError = z.infer<typeof apiErrorSchema>;
